/**
 * OpenTelemetry web SDK bootstrap (replaces Application Insights).
 *
 * Registers a WebTracerProvider with an OTLP/HTTP trace exporter that POSTs
 * spans to the same OpenObserve collector the backend uses — reached via a
 * public OTLP ingress with a scoped `Bearer` ingest token (endpoint + token
 * injected at container start into window.__STREAMKO_CONFIG__; see
 * runtimeConfig.ts). When no endpoint is configured (dev, or observability
 * disabled) we don't register a provider, so the global tracer stays a no-op
 * and `src/lib/telemetry.ts` degrades silently.
 */
import { trace } from '@opentelemetry/api';
import { WebTracerProvider, BatchSpanProcessor } from '@opentelemetry/sdk-trace-web';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions';
import { getOtelConfig } from './runtimeConfig';

export const OTEL_SERVICE_NAME = 'streamko-web';
/** Instrumentation scope name — use the same in telemetry.ts. */
export const OTEL_TRACER_NAME = 'streamko-web';

let initialized = false;

/** Parse an OTEL_RESOURCE_ATTRIBUTES-style `key=value,key=value` string. */
function parseResourceAttributes(spec?: string): Record<string, string> {
  if (!spec) return {};
  return Object.fromEntries(
    spec
      .split(',')
      .map((pair) => pair.split('='))
      .filter(([k, v]) => k && v !== undefined)
      .map(([k, v]) => [k.trim(), v.trim()]),
  );
}

/** Initialize OTel once. Safe no-op in SSR/test, or when no collector is set. */
export function initOtel(): void {
  if (initialized || typeof window === 'undefined') return;
  const cfg = getOtelConfig();
  if (!cfg) return;
  initialized = true;

  const spanProcessors = [
    new BatchSpanProcessor(
      new OTLPTraceExporter({
        url: cfg.endpoint,
        // `token` is the full Authorization header value the collector expects —
        // for OpenObserve a service account's `Basic base64(email:token)`.
        headers: cfg.token ? { Authorization: cfg.token } : undefined,
      }),
    ),
  ];

  // Dual-ship: additionally export to OneUptime when a second endpoint is
  // configured, mirroring the backend otel-gateway fan-out (SigNoz primary +
  // OneUptime APM eval). OneUptime authenticates with an `x-oneuptime-token`
  // header (NOT Authorization). A SEPARATE BatchSpanProcessor gives it its own
  // queue, so a OneUptime stall can't back-pressure the SigNoz exporter.
  if (cfg.oneUptimeEndpoint) {
    spanProcessors.push(
      new BatchSpanProcessor(
        new OTLPTraceExporter({
          url: cfg.oneUptimeEndpoint,
          headers: cfg.oneUptimeToken
            ? { 'x-oneuptime-token': cfg.oneUptimeToken }
            : undefined,
        }),
      ),
    );
  }

  const provider = new WebTracerProvider({
    resource: resourceFromAttributes({
      [ATTR_SERVICE_NAME]: OTEL_SERVICE_NAME,
      ...parseResourceAttributes(cfg.resourceAttributes),
    }),
    spanProcessors,
  });

  // No ZoneContextManager / propagators: we only emit fire-and-forget spans
  // for custom events, exceptions, page views, and timed traces — there's no
  // distributed-trace context to maintain in the browser.
  provider.register();
}

/** The web tracer (a no-op tracer until/unless initOtel registers a provider). */
export function getWebTracer() {
  return trace.getTracer(OTEL_TRACER_NAME);
}
